import type { VehicleImageUrls } from '@lucid/lego';
import type { FormField } from '@lucid/lego';
import type { VinValidationResult, FormSubmissionData, VehicleData, CaseType } from './types';
import {
  VIN_LENGTH,
  VIN_VALID_CHARACTERS,
  VIN_CHECK_DIGIT_POSITION,
  VIN_TRANSLITERATION,
  VIN_POSITION_WEIGHTS,
  LUCID_IMAGE_BASE_URL,
  COLOR_TO_URL_MAP,
  MODEL_TO_TRIM_MAP,
  CASE_PARENT_CATEGORY,
  CASE_SUB_CATEGORY,
} from './constants';
import {
  techSupportFormFields,
  escalationFormFields,
} from '../../pages/CustomerCare/CreateCase/formFields';

/**
 * Quick check if a string looks like a VIN (17 characters)
 * @param value - The string to check
 * @returns True if the string is 17 characters long
 */
export const looksLikeVin = (value: string): boolean => {
  return value.length === VIN_LENGTH;
};

/**
 * Format VIN input to uppercase and remove invalid characters
 * @param value - The raw input value
 * @returns Formatted VIN string
 */
export const formatVinInput = (value: string): string => {
  return value.toUpperCase().replace(/[IOQ]/g, '').slice(0, VIN_LENGTH);
};

/**
 * Calculate the VIN check digit
 * @param vin - The VIN string (17 characters)
 * @returns The calculated check digit (0-9 or X)
 */
const calculateCheckDigit = (vin: string): string => {
  let sum = 0;

  for (let i = 0; i < vin.length; i++) {
    const char = vin[i].toUpperCase();
    const value = VIN_TRANSLITERATION[char];
    const weight = VIN_POSITION_WEIGHTS[i];

    if (value !== undefined && weight !== undefined) {
      sum += value * weight;
    }
  }

  const remainder = sum % 11;
  return remainder === 10 ? 'X' : String(remainder);
};

/**
 * Validate a VIN (Vehicle Identification Number)
 * Performs the following checks:
 * 1. Length must be exactly 17 characters
 * 2. Characters must be valid (A-Z except I, O, Q, and 0-9)
 * 3. Check digit validation (position 9)
 *
 * @param vin - The VIN to validate
 * @returns Validation result with isValid flag and optional error message
 */
export const validateVin = (vin: string): VinValidationResult => {
  if (!vin) {
    return { isValid: false, error: 'VIN is required' };
  }

  const trimmedVin = vin.trim().toUpperCase();

  // Check length
  if (trimmedVin.length !== VIN_LENGTH) {
    return {
      isValid: false,
      error: `VIN must be exactly ${VIN_LENGTH} characters (current: ${trimmedVin.length})`,
    };
  }

  // Check for invalid characters (I, O, Q are not allowed)
  if (!VIN_VALID_CHARACTERS.test(trimmedVin)) {
    const invalidChars = trimmedVin.match(/[IOQ]/gi);
    if (invalidChars) {
      return {
        isValid: false,
        error: `VIN cannot contain letters I, O, or Q. Found: ${[...new Set(invalidChars)].join(', ')}`,
      };
    }
    return {
      isValid: false,
      error: 'VIN contains invalid characters. Only A-Z (except I, O, Q) and 0-9 are allowed.',
    };
  }

  // Validate check digit (position 9)
  const expectedCheckDigit = calculateCheckDigit(trimmedVin);
  const actualCheckDigit = trimmedVin[VIN_CHECK_DIGIT_POSITION];

  if (actualCheckDigit !== expectedCheckDigit) {
    return {
      isValid: false,
      error: `Invalid VIN check digit. Expected '${expectedCheckDigit}' at position 9, got '${actualCheckDigit}'`,
    };
  }

  return { isValid: true };
};

/**
 * Get form fields based on case type
 * @param caseType - The type of case
 * @returns Array of form fields
 */
export const getFormFieldsByCaseType = (caseType: CaseType): FormField[] => {
  if (caseType === 'escalation') {
    return escalationFormFields;
  }
  return techSupportFormFields;
};

/**
 * Build form submission data payload
 * @param formData - Raw form data
 * @param vehicleData - Vehicle information
 * @param caseType - Type of case being created
 * @param isDraft - Whether this is a draft save
 * @returns Formatted submission data
 */
export const buildFormSubmissionData = (
  formData: Record<string, unknown>,
  vehicleData: VehicleData | null,
  caseType: CaseType,
  isDraft = false
): FormSubmissionData => {
  return {
    caseParentCategory: CASE_PARENT_CATEGORY.VIN_RELATED,
    caseSubCategory:
      caseType === 'escalation'
        ? CASE_SUB_CATEGORY.ESCALATION
        : CASE_SUB_CATEGORY.TECH_SUPPORT_NON_SERVICE,
    priority: (formData.priority as string) || '',
    caseSubject: (formData.caseSubject as string) || '',
    caseSummary: (formData.caseSummary as string) || '',
    requestedSupport: (formData.requestedSupport as string) || '',
    vin: vehicleData?.details.vin || '',
    locationOfEvent: (formData.locationOfEvent as string) || '',
    stepsTaken: (formData.stepsTaken as string) || '',
    attachMedia: (formData.attachMedia as string[]) || [],
    status: '',
    customerName: vehicleData?.customer.name || '',
    customerEmail: vehicleData?.customer.email || '',
    customerPhone: vehicleData?.customer.phone || '',
    draft: isDraft ? 'draft' : '',
  };
};

/**
 * Get vehicle image URLs from Lucid CDN based on model and color
 * @param model - Vehicle model (e.g., "Lucid Air Pure")
 * @param imageColor - Simple color name (e.g., "white", "black")
 * @returns Full image URLs object for the Image component
 */
export const getVehicleImageUrl = (model: string, imageColor: string): VehicleImageUrls => {
  // Extract trim from model name (e.g., "Lucid Air Pure" -> "pure")
  const modelLower = model.toLowerCase();
  let trim = 'touring'; // Default fallback

  for (const [key, value] of Object.entries(MODEL_TO_TRIM_MAP)) {
    if (modelLower.includes(key)) {
      trim = value;
      break;
    }
  }

  // Get URL color name
  const urlColor = COLOR_TO_URL_MAP[imageColor?.toLowerCase() || 'white'] || 'stellar-white';
  const baseName = `lucid-air-${trim}-${urlColor}`;

  return {
    uri_mobile: {
      '1x': `${LUCID_IMAGE_BASE_URL}/${baseName}-mobile.webp`,
      '2x': `${LUCID_IMAGE_BASE_URL}/${baseName}-mobile_2x.webp`,
    },
    uri_tablet: {
      '1x': `${LUCID_IMAGE_BASE_URL}/${baseName}-tablet.webp`,
      '2x': `${LUCID_IMAGE_BASE_URL}/${baseName}-tablet_2x.webp`,
    },
    uri_desktop: {
      '1x': `${LUCID_IMAGE_BASE_URL}/${baseName}-desktop.webp`,
      '2x': `${LUCID_IMAGE_BASE_URL}/${baseName}-desktop_2x.webp`,
    },
    alt: baseName,
  };
};

/**
 * Format case identifier for display
 * @param identifier - Full case identifier
 * @returns Formatted case ID (last 12 characters)
 */
export const formatCaseIdentifier = (identifier: string): string => {
  if (!identifier) return '-';
  return `Case-${identifier.slice(-12)}`;
};
